import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Section } from '../../greek-revision/types/models';
import { FirestoreService } from '../services/FirestoreService';
import { RevisionSystemConfig } from '../config/RevisionSystemConfig';
import { AuthContext } from '../../AuthContext';

/**
 * User information for the Revision System
 */
interface User {
  uid: string;
  username: string;
  email: string;
}

/**
 * Global application state
 */
interface AppState {
  user: User | null;
  sections: Section[];
  currentSection: Section | null;
  isLoading: boolean;
}

/**
 * Context value with state and actions
 */
interface AppContextValue extends AppState {
  config: RevisionSystemConfig;
  setUser: (user: User | null) => void;
  setSections: (sections: Section[]) => void;
  setCurrentSection: (section: Section | null) => void;
  setIsLoading: (loading: boolean) => void;
  refreshSections: () => Promise<void>;
  firestoreService: FirestoreService;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

/**
 * Props for the RevisionProvider component
 */
interface RevisionProviderProps {
  config: RevisionSystemConfig;
  children: ReactNode;
}

/**
 * Provider component for global app state
 * Accepts configuration to customize behavior for different revision systems
 */
export const RevisionProvider: React.FC<RevisionProviderProps> = ({ config, children }) => {
  const { loggedInUser } = useContext(AuthContext);
  const [user, setUser] = useState<User | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Create FirestoreService instance with configuration
  const [firestoreService] = useState(() => new FirestoreService(config));

  /**
   * Update user when loggedInUser changes
   */
  useEffect(() => {
    if (loggedInUser) {
      setUser({
        uid: loggedInUser.uid,
        username: loggedInUser.username,
        email: loggedInUser.email
      });
      firestoreService.setUserId(loggedInUser.uid);
    } else {
      setUser(null);
    }
  }, [loggedInUser]);

  /**
   * Load sections from Firestore when user changes
   */
  useEffect(() => {
    const loadSections = async () => {
      if (!user) {
        setSections([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Ensure userId is set before loading sections
        firestoreService.setUserId(user.uid);
        const loadedSections = await firestoreService.getAllSections();
        setSections(loadedSections);
      } catch (error) {
        console.error('Error loading sections:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSections();
  }, [user]);

  /**
   * Refresh sections from Firestore
   */
  const refreshSections = async () => {
    if (!user) {
      return;
    }
    
    try {
      setIsLoading(true);
      // Ensure userId is set before refreshing sections
      firestoreService.setUserId(user.uid);
      const loadedSections = await firestoreService.getAllSections();
      setSections(loadedSections);
    } catch (error) {
      console.error('Error refreshing sections:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: AppContextValue = {
    config,
    user,
    sections,
    currentSection,
    isLoading,
    setUser,
    setSections,
    setCurrentSection,
    setIsLoading,
    refreshSections,
    firestoreService,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

/**
 * Hook to access revision context
 */
export const useRevisionContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useRevisionContext must be used within a RevisionProvider');
  }
  return context;
};

/**
 * Alias for useRevisionContext for backward compatibility
 */
export const useAppContext = useRevisionContext;
